"use client";

import { useEffect, useRef, useState } from "react";

import portrait1 from "@/assets/website/portrait-1.jpg";
import portrait2 from "@/assets/website/portrait-2.jpg";
import portrait3 from "@/assets/website/portrait-3.jpg";

// folktale.ai's three animated "How it works" illustrations, ported 1:1.
// Each scene is a pure function of a looping clock `t` (seconds).

const PAPER = "var(--color-site-cream)";
const INK = "var(--color-site-forest-deep)";
const GREEN = "var(--color-site-forest)";
const CLAY = "var(--color-site-clay)";
const TERRACOTTA = "var(--color-site-terracotta)";

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const easeOutCubic = (v: number) => 1 - Math.pow(1 - v, 3);
const easeInOutQuad = (v: number) =>
  v < 0.5 ? 2 * v * v : 1 - Math.pow(-2 * v + 2, 2) / 2;

/** Looping clock that only runs while the card is on screen. */
function useLoopClock(period: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [t, setT] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      // Hold on the finished frame; deferred so the effect doesn't setState synchronously.
      const id = requestAnimationFrame(() => setT(period * 0.9));
      return () => cancelAnimationFrame(id);
    }
    let frame = 0;
    let start = 0;
    let running = false;
    const tick = (now: number) => {
      if (!start) start = now;
      setT(((((now - start) / 1000) % period) + period) % period);
      frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !running) {
          running = true;
          frame = requestAnimationFrame(tick);
        } else if (!entry.isIntersecting && running) {
          running = false;
          cancelAnimationFrame(frame);
          start = 0;
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [period]);

  return { ref, t };
}

function Card({
  innerRef,
  children,
}: {
  innerRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}) {
  return (
    <div
      ref={innerRef}
      className="border-site-forest-deep/10 bg-site-cream w-full overflow-hidden rounded-2xl border shadow-sm"
    >
      <svg viewBox="0 0 400 260" className="h-full w-full" role="presentation">
        <rect width="400" height="260" fill={PAPER} />
        {children}
      </svg>
    </div>
  );
}

const polar = (
  cx: number,
  cy: number,
  r: number,
  deg: number,
): [number, number] => {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

/** Donut segment path from angle `from` to `to` (degrees, 0 = top). */
function arc(
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  from: number,
  to: number,
) {
  const large = to - from > 180 ? 1 : 0;
  const [x1, y1] = polar(cx, cy, outer, from);
  const [x2, y2] = polar(cx, cy, outer, to);
  const [x3, y3] = polar(cx, cy, inner, to);
  const [x4, y4] = polar(cx, cy, inner, from);
  return `M${x1} ${y1} A${outer} ${outer} 0 ${large} 1 ${x2} ${y2} L${x3} ${y3} A${inner} ${inner} 0 ${large} 0 ${x4} ${y4} Z`;
}

const round2 = (v: number) => Math.round(v * 100) / 100;

/** Adds each segment's start angle so render never needs a running counter. */
function withStarts<T extends { span: number }>(segments: T[]) {
  let from = 0;
  return segments.map((seg) => {
    const out = { ...seg, from };
    from += seg.span;
    return out;
  });
}

// Step 1 — scattered evidence dots gather into a ring, then a donut fills in.
const SCATTER = Array.from({ length: 26 }, (_, i) => {
  const a = (i * 137.5 * Math.PI) / 180;
  return {
    x: 200 + Math.cos(a) * (150 + (i % 5) * 12),
    y: 130 + Math.sin(a) * (105 + (i % 4) * 10),
    r: 2.5 + (i % 3),
    delay: (i % 9) * 0.12,
    ring: i % 3,
  };
});
const SCATTER_COLOURS = [TERRACOTTA, GREEN, CLAY];
const GATHER_SEGMENTS = withStarts([
  { span: 118, colour: GREEN },
  { span: 96, colour: CLAY },
  { span: 84, colour: TERRACOTTA },
  { span: 62, colour: INK },
]);

function GatherScene() {
  const { ref, t } = useLoopClock(6);
  const fill = easeOutCubic(clamp01((t - 1.6) / 1.8));
  return (
    <Card innerRef={ref}>
      <circle
        cx="200"
        cy="130"
        r="66"
        fill="none"
        stroke={INK}
        strokeOpacity="0.12"
        strokeWidth="1"
        strokeDasharray="4 5"
      />
      {SCATTER.map((dot, i) => {
        const p = easeInOutQuad(clamp01((t - dot.delay) / 1.7));
        const [tx, ty] = polar(200, 130, 52, (i * 360) / SCATTER.length);
        return (
          <circle
            key={i}
            cx={round2(dot.x + (tx - dot.x) * p)}
            cy={round2(dot.y + (ty - dot.y) * p)}
            r={dot.r * (1 - 0.35 * p)}
            fill={SCATTER_COLOURS[dot.ring]}
            opacity={0.25 + 0.55 * p - fill * 0.8}
          />
        );
      })}
      {GATHER_SEGMENTS.map((seg, i) => {
        const { from } = seg;
        const to = Math.min(from + seg.span, 360 * fill);
        return to <= from ? null : (
          <path
            key={i}
            d={arc(200, 130, 78, 50, from, to)}
            fill={seg.colour}
            fillOpacity={i === 3 ? 0.35 : 0.75}
          />
        );
      })}
      <circle cx="200" cy="130" r="28" fill={PAPER} opacity={fill} />
      <circle
        cx="200"
        cy="130"
        r="8"
        fill={GREEN}
        opacity={fill * clamp01((t - 3.2) / 0.6)}
      />
    </Card>
  );
}

// Step 2 — bars and donut grow, then the missing pieces pulse as a gap.
const GAP_SEGMENTS = withStarts([
  { span: 104, colour: GREEN },
  { span: 82, colour: CLAY },
  { span: 58, colour: INK },
]);
const GAP_SPAN = 116;
const BAR_HEIGHTS = [52, 74, 40, 88, 30];

function GapScene() {
  const { ref, t } = useLoopClock(6.5);
  const grow = easeOutCubic(clamp01(t / 1.4));
  const reveal = clamp01((t - 2) / 1);
  const pulse = 0.5 + 0.5 * Math.sin(t * 3.4);
  return (
    <Card innerRef={ref}>
      {BAR_HEIGHTS.map((full, i) => {
        const h = full * easeOutCubic(clamp01((t - i * 0.12) / 1));
        const missing = i === 2 || i === 4;
        return (
          <rect
            key={i}
            x={40 + i * 26}
            y={196 - h}
            width="16"
            height={h}
            rx="4"
            fill={missing ? INK : GREEN}
            fillOpacity={missing ? 0.12 + reveal * 0.05 : 0.55}
            stroke={missing ? TERRACOTTA : "none"}
            strokeOpacity={missing ? reveal * (0.35 + pulse * 0.45) : 0}
            strokeDasharray="4 4"
          />
        );
      })}
      <line
        x1="34"
        y1="198"
        x2="176"
        y2="198"
        stroke={INK}
        strokeOpacity="0.2"
      />
      {GAP_SEGMENTS.map((seg, i) => {
        const { from } = seg;
        const to = Math.min(from + seg.span, 360 * grow);
        return to <= from ? null : (
          <path
            key={i}
            d={arc(288, 122, 74, 46, from, to)}
            fill={seg.colour}
            fillOpacity={i === 2 ? 0.3 : 0.7}
          />
        );
      })}
      <path
        d={arc(288, 122, 74 + reveal * 6, 46, 244, 244 + GAP_SPAN)}
        fill={INK}
        fillOpacity={0.05 + reveal * 0.03}
        stroke={TERRACOTTA}
        strokeOpacity={reveal * (0.4 + pulse * 0.5)}
        strokeWidth="2"
        strokeDasharray="6 5"
      />
      <circle
        cx="288"
        cy="122"
        r="26"
        fill={TERRACOTTA}
        fillOpacity={reveal * 0.12}
      />
      <g
        opacity={reveal}
        stroke={TERRACOTTA}
        strokeWidth="3"
        strokeLinecap="round"
      >
        <line x1="288" y1="110" x2="288" y2="124" />
        <line x1="288" y1="132" x2="288" y2="132" />
      </g>
    </Card>
  );
}

// Step 3 — a network of voices; three faces fly into the gap and close it.
const NODES = [
  { x: 68, y: 70, r: 9, c: GREEN },
  { x: 120, y: 42, r: 6, c: CLAY },
  { x: 46, y: 132, r: 7, c: CLAY },
  { x: 108, y: 122, r: 13, c: TERRACOTTA },
  { x: 74, y: 198, r: 8, c: GREEN },
  { x: 142, y: 182, r: 6, c: TERRACOTTA },
  { x: 156, y: 100, r: 9, c: GREEN },
];
const EDGES: [number, number][] = [
  [0, 1],
  [0, 3],
  [2, 3],
  [3, 4],
  [3, 5],
  [3, 6],
  [1, 6],
  [4, 5],
];
const FACES = [
  { node: 0, src: portrait1.src },
  { node: 3, src: portrait2.src },
  { node: 6, src: portrait3.src },
];

function FillScene() {
  const { ref, t } = useLoopClock(7);
  const fill = easeOutCubic(clamp01((t - 2.6) / 2));

  // The network shrinks towards the donut and fades as the faces fly off.
  const shrink = easeInOutQuad(clamp01((t - 2.2) / 1.7));
  const [targetX, targetY] = polar(292, 130, 60, 302);
  const scale = 1 - 0.82 * shrink;
  const originX = 102;
  const originY = 120;
  const cx = originX + (targetX - originX) * shrink;
  const cy = originY + (targetY - originY) * shrink;

  return (
    <Card innerRef={ref}>
      <defs>
        {FACES.map((face, i) => (
          <clipPath key={`src-${i}`} id={`gap-face-src-${i}`}>
            <circle cx={NODES[face.node].x} cy={NODES[face.node].y} r={18} />
          </clipPath>
        ))}
        {FACES.map((_, i) => (
          <clipPath key={`fly-${i}`} id={`gap-face-fly-${i}`}>
            <circle cx="0" cy="0" r={16} />
          </clipPath>
        ))}
        <filter id="gap-face-grey">
          <feColorMatrix
            type="matrix"
            values="0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0"
          />
        </filter>
      </defs>

      <g
        transform={`translate(${cx - originX * scale} ${cy - originY * scale}) scale(${scale})`}
        opacity={1 - shrink}
      >
        {EDGES.map(([a, b], i) => {
          const p = easeOutCubic(clamp01((t - 0.4 - i * 0.16) / 0.7));
          const from = NODES[a];
          const to = NODES[b];
          return (
            <line
              key={i}
              x1={from.x}
              y1={from.y}
              x2={from.x + (to.x - from.x) * p}
              y2={from.y + (to.y - from.y) * p}
              stroke={INK}
              strokeOpacity="0.25"
              strokeWidth="1.5"
            />
          );
        })}
        {NODES.map((node, i) => {
          const p = easeOutCubic(clamp01((t - i * 0.12) / 0.6));
          return (
            <g key={i}>
              <circle
                cx={node.x}
                cy={node.y}
                r={node.r * p * 2.1}
                fill={node.c}
                fillOpacity={0.1 * p}
              />
              <circle
                cx={node.x}
                cy={node.y}
                r={node.r * p}
                fill={node.c}
                fillOpacity={0.85}
              />
            </g>
          );
        })}
        {FACES.map((face, i) => {
          const node = NODES[face.node];
          const p = easeOutCubic(clamp01((t - 0.5 - i * 0.2) / 0.7));
          return p <= 0 ? null : (
            <g key={`face-${i}`} opacity={p}>
              <image
                href={face.src}
                x={node.x - 18}
                y={node.y - 18}
                width={36}
                height={36}
                preserveAspectRatio="xMidYMid slice"
                clipPath={`url(#gap-face-src-${i})`}
              />
              <circle
                cx={node.x}
                cy={node.y}
                r={18}
                fill="none"
                stroke={PAPER}
                strokeWidth="2.5"
              />
            </g>
          );
        })}
      </g>

      {GAP_SEGMENTS.map((seg, i) => {
        return (
          <path
            key={i}
            d={arc(292, 130, 74, 46, seg.from, seg.from + seg.span)}
            fill={seg.colour}
            fillOpacity={i === 2 ? 0.3 : 0.7}
          />
        );
      })}
      {fill > 0 ? (
        <path
          d={arc(292, 130, 74, 46, 244, 244 + GAP_SPAN * fill)}
          fill={TERRACOTTA}
          fillOpacity="0.8"
        />
      ) : null}
      {fill < 1 ? (
        <path
          d={arc(292, 130, 74, 46, 244 + GAP_SPAN * fill, 244 + GAP_SPAN)}
          fill={INK}
          fillOpacity="0.05"
          stroke={INK}
          strokeOpacity="0.2"
          strokeDasharray="6 5"
        />
      ) : null}
      <circle cx="292" cy="130" r="34" fill={PAPER} />

      {FACES.map((face, i) => {
        const raw = clamp01((t - 2.2 - i * 0.45) / 1.1);
        if (raw <= 0) return null;
        const p = easeInOutQuad(Math.min(1, raw));
        const node = NODES[face.node];
        const [dx, dy] = polar(292, 130, 60, 268 + i * 34);
        const x = node.x + (dx - node.x) * p;
        const y = node.y + (dy - node.y) * p;
        const landed = clamp01((raw - 0.85) / 0.15);
        return (
          <g key={`fly-${i}`} transform={`translate(${x} ${y})`}>
            <image
              href={face.src}
              x={-16}
              y={-16}
              width={32}
              height={32}
              preserveAspectRatio="xMidYMid slice"
              clipPath={`url(#gap-face-fly-${i})`}
              filter={landed > 0.5 ? undefined : "url(#gap-face-grey)"}
            />
            <circle r={16} fill="none" stroke={PAPER} strokeWidth="2.5" />
            <circle
              r={16}
              fill="none"
              stroke={TERRACOTTA}
              strokeOpacity={landed}
              strokeWidth="2"
            />
          </g>
        );
      })}

      <g opacity={clamp01((t - 4.8) / 0.6)}>
        <circle cx="292" cy="130" r="20" fill={GREEN} fillOpacity="0.15" />
        <path
          d="M283 130 l6 7 l13 -15"
          fill="none"
          stroke={GREEN}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
    </Card>
  );
}

export default function StepVisual({ step }: { step: 1 | 2 | 3 }) {
  if (step === 1) return <GatherScene />;
  if (step === 2) return <GapScene />;
  return <FillScene />;
}
